<template>
  <div style="background-color:black; height:100%">
    <!-- Add Purchase Order Page -->
    <v-row justify="center">
      <v-col cols="12" md="12">
        <v-card class="add-po-card">
          <!-- Back Button -->
          <v-btn icon class="back-button" @click="$router.go(-1)">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <!-- Card Title -->
          <v-card-title class="headline">Add Purchase Order</v-card-title>
          <!-- Card Content -->
          <v-card-text>
            <v-form ref="form" v-model="valid">
              <!-- First Row: Purchase Number, Nexus PO Number, Supplier, Supplier Reference -->
              <v-row>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="purchaseOrders[0].purchaseNumber"
                    label="Purchase Number"
                    dense
                    outlined
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="purchaseOrders[0].nexusPONumber"
                    label="Nexus PO Number"
                    dense
                    outlined
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-autocomplete
                    v-model="purchaseOrders[0].supplier"
                    :items="suppliers"
                    item-text="supplierName"
                    item-value="supplierName"
                    label="Supplier"
                    dense
                    outlined
                    :rules="[rules.required]"
                    clearable
                    solo
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="purchaseOrders[0].supplierReference"
                    label="Supplier Reference"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <!-- Second Row: Combined Delivery Date (only), Priority, SO Number, Quote Timestamp -->
              <v-row>
                <v-col cols="12" sm="3">
                  <!-- Combined Delivery Date Picker (date only) -->
                  <v-menu
                    v-model="purchaseOrders[0].deliveryDateTimeMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="purchaseOrders[0].deliveryDateTimeDisplay"
                        label="Delivery Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        dense
                        outlined
                        v-bind="attrs"
                        v-on="on"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="purchaseOrders[0].deliveryDate"
                      :min="minDate"
                      no-title
                      scrollable
                      @input="updatePODeliveryDateTime(0)"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="purchaseOrders[0].priority"
                    :items="priorityOptions"
                    label="Priority"
                    dense
                    outlined
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="purchaseOrders[0].soNumber"
                    label="SO Number"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="purchaseOrders[0].quoteTimestamp"
                    label="Quote Timestamp"
                    dense
                    outlined
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>
              <!-- Third Row: Engineer, Salesperson, Status -->
              <v-row>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="purchaseOrders[0].selectedEngineer"
                    :items="engineers"
                    item-text="fullName"
                    item-value="id"
                    label="Engineer"
                    dense
                    outlined
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="purchaseOrders[0].selectedSalesperson"
                    :items="salespersons"
                    item-text="fullName"
                    item-value="id"
                    label="Salesperson"
                    dense
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="purchaseOrders[0].status"
                    :items="status"
                    label="Status"
                    dense
                    outlined
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
              </v-row>
              <!-- Notes Field as a Textarea on its Own Line -->
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="purchaseOrders[0].notes"
                    label="Notes"
                    dense
                    outlined
                    rows="3"
                  ></v-textarea>
                </v-col>
              </v-row>
              <!-- Items Section -->
              <h3 class="section-title my-4">Items</h3>
              <div
                v-for="(item, index) in purchaseOrders[0].items"
                :key="index"
                class="item-group"
              >
                <!-- The five fields are now placed in a single row using inline flex styles (each taking 20% width) -->
                <v-row class="d-flex align-center">
                  <v-col style="flex: 0 0 10%;">
                    <v-text-field
                      v-model="item.quantity"
                      label="Quantity"
                      dense
                      outlined
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                  <v-col style="flex: 0 0 20%;">
                    <v-text-field
                      v-model="item.code"
                      label="Code"
                      dense
                      outlined
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                  <v-col style="flex: 0 0 20%;">
                    <v-text-field
                      v-model="item.description"
                      label="Description"
                      dense
                      outlined
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                  <v-col style="flex: 0 0 20%;">
                    <v-text-field
                      v-model="item.drawingNumber"
                      label="Drawing Number"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col style="flex: 0 0 20%;">
                    <v-select
                      v-model="item.leadtime"
                      :items="leadTimeOptions"
                      label="Lead Time"
                      outlined
                      dense
                      :rules="[rules.required]"
                    ></v-select>
                    
                  </v-col>
                  <v-btn
                      dark
                      color="red"
                      dense
                      @click="removePOItem(0, index)"
                      class="montserrat-button my-2"
                    >
                      <v-icon left>mdi-delete</v-icon>
                    </v-btn>
                </v-row>
                <!-- The Remove button is placed on a separate row and aligned to the right -->
                <v-row>
                  <v-col cols="12" class="text-right">
                    
                  </v-col>
                </v-row>
              </div>
              <v-row justify="start" class="pa-2">
                <v-btn
                  dark
                  color="blue"
                  dense
                  @click="addItem(0)"
                  class="montserrat-button"
                >
                  <v-icon left>mdi-plus</v-icon>Add Item
                </v-btn>
              </v-row>
              <!-- Button to open Delivery Dialog -->
              <div v-if="valid && purchaseOrders.length > 0">
                <v-row justify="start" class="mt-4">
                  <v-btn
                    dark
                    dense
                    large
                    @click="openDeliveryDialog"
                    class="montserrat-button"
                  >
                    <v-icon left>mdi-plus</v-icon>Add Delivery
                  </v-btn>
                </v-row>
              </div>
              <v-row justify="center" class="mt-4">
                <v-btn
  dark
  dense
  large
  @click="confirmSubmit"
  class="montserrat-button"
>
  <v-icon left>mdi-check-circle</v-icon>Submit Purchase Orders
</v-btn>

              </v-row>
            </v-form>
            <v-overlay :value="loading">
              <v-progress-circular
                :size="100"
                color="white"
                indeterminate
              ></v-progress-circular>
            </v-overlay>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delivery Dialog -->
    <v-dialog v-model="showDeliveryDialog" max-width="1200px">
      <v-card>
        <v-card-title class="headline">Add Deliveries / Collections</v-card-title>
        <v-card-text>
          <v-form ref="deliveryForm" v-model="deliveryValid">
            <v-row>
              <!-- First Row -->
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="delivery.deliveryNumber"
                  label="Delivery Number"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-package-variant-closed"
                  class="montserrat-input"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="delivery.method"
                  :items="methods"
                  label="Delivery Method"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-truck"
                  class="montserrat-input"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="delivery.type"
                  :items="filteredTypes"
                  label="Type"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-transfer"
                  class="montserrat-input"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="delivery.driverName"
                  label="Driver Name"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[isInTransit ? rules.required : []]"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <!-- Second Row -->
              <v-col cols="12" sm="3">
                <!-- PO Number is now the Nexus PO Number -->
                <v-text-field
                  v-model="delivery.poNumber"
                  label="PO Number (Nexus PO Number)"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-numeric"
                  class="montserrat-input"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-autocomplete
                  v-model="delivery.location"
                  :items="combinedLocations"
                  label="Location (Suppliers & Customers)"
                  outlined
                  rounded
                  dense
                  item-text="name"
                  item-value="account"
                  prepend-inner-icon="mdi-map-marker"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :loading="loadingLocations"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="3">
    <v-text-field
      v-model="delivery.engineer"
      label="Engineer"
      outlined
      dense
      readonly
      prepend-inner-icon="mdi-account"
      class="montserrat-input"
    ></v-text-field>
  </v-col>
  <v-col cols="12" sm="3">
    <v-text-field
      v-model="delivery.salesperson"
      label="Salesperson"
      outlined
      dense
      readonly
      prepend-inner-icon="mdi-account"
      class="montserrat-input"
    ></v-text-field>
  </v-col>
            </v-row>
            
            <v-row>
              <!-- Third Row -->
              <v-col cols="12" sm="3">
                <v-select
                  v-model="delivery.urgency"
                  :items="urgencies"
                  label="Priority"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-alert-circle"
                  class="montserrat-input"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="3">
                <!-- Combined Delivery Date Picker (date only) -->
                <v-menu
                  v-model="delivery.datetimeMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="delivery.datetimeDisplay"
                      label="Delivery Date"
                      outlined
                      rounded
                      dense
                      prepend-inner-icon="mdi-calendar"
                      class="montserrat-input"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="delivery.date"
                    :min="minDate"
                    no-title
                    scrollable
                    @input="updateDeliveryDateTime"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="delivery.status"
                  :items="statuses"
                  label="Status"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-truck-delivery"
                  class="montserrat-input"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
  <v-col cols="12" sm="3">
    <v-text-field
      v-model="delivery.soNumber"
      label="SO Number"
      outlined
      rounded
      dense
      prepend-inner-icon="mdi-numeric"
      class="montserrat-input"
      readonly
    ></v-text-field>
  </v-col>
</v-row>
            <v-row>
              <!-- Reason Field -->
              <v-col cols="12">
                <v-textarea
                  v-model="delivery.reason"
                  label="Reason for Delivery/Collection"
                  outlined
                  rounded
                  dense
                  prepend-inner-icon="mdi-comment-alert"
                  class="montserrat-input"
                  :rules="[reasonRequired ? rules.required : []]"
                ></v-textarea>
              </v-col>
            </v-row>
            <!-- Dynamic Delivery Items Section -->
            <v-row>
              <h2 class="red--text mx-12" style="margin-bottom: 30px;">DELIVERY ITEMS</h2>
            </v-row>
            <div v-for="(item, index) in deliveryItems" :key="index">
              <v-row>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="item.qty"
                    label="QTY"
                    outlined
                    rounded
                    dense
                    prepend-inner-icon="mdi-numeric"
                    class="montserrat-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="item.code"
                    label="Code"
                    outlined
                    rounded
                    dense
                    prepend-inner-icon="mdi-numeric"
                    class="montserrat-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="item.description"
                    label="Description"
                    outlined
                    rounded
                    dense
                    prepend-inner-icon="mdi-numeric"
                    class="montserrat-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="item.refnum"
                    label="Reference Number"
                    outlined
                    rounded
                    dense
                    prepend-inner-icon="mdi-numeric"
                    class="montserrat-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="2" v-if="delivery.type === 'Both'">
                  <v-select
                    v-model="item.for"
                    :items="['Delivery', 'Collection']"
                    label="For"
                    outlined
                    rounded
                    dense
                    class="montserrat-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="1">
                  <v-btn icon @click="removeDeliveryItem(index)" color="red">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
            <v-row justify="left" class="mt-3">
              <v-btn
                color="black"
                outlined
                dark
                width="300px"
                rounded
                @click="addDeliveryItem"
              >
                <v-icon left>mdi-plus-circle-outline</v-icon>Add More Delivery Items
              </v-btn>
            </v-row>
            <v-row justify="center">
              <v-btn
                dark
                rounded
                large
                width="300px"
                @click="saveDelivery"
                class="montserrat-button my-3"
              >
                <v-icon left>mdi-briefcase-plus</v-icon>Save
              </v-btn>
            </v-row>
          </v-form>
          <v-progress-circular
            v-if="loading"
            indeterminate
            color="primary"
            size="70"
            class="my-4"
          ></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogSubmit" max-width="500px">
  <v-card>
    <v-card-title class="headline">Confirm Submission</v-card-title>
    <v-card-text>Are you sure you want to submit this purchase order?</v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="red darken-1" text @click="dialogSubmit = false">Cancel</v-btn>
      <v-btn color="green darken-1" text @click="submitConfirmed">Confirm</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

export default {
  data() {
    return {
      valid: false,
      deliveryValid: false,
      loading: false,
      loadingLocations: false,
      menu: false,
      minDate: new Date().toISOString().substr(0, 10),
      purchaseOrders: [
        {
          purchaseNumber: "",
          nexusPONumber: "",
          supplier: null,
          supplierReference: "",
          // Combined delivery date field for PO (date only)
          deliveryDate: null,
          deliveryDateTimeMenu: false,
          deliveryDateTimeDisplay: "",
          priority: null,
          notes: "",
          soNumber: "",
          selectedEngineer: null,
          selectedSalesperson: null,
          quoteTimestamp: new Date().toISOString(),
          items: [
            {
              quantity: "",
              code: "",
              description: "",
              drawingNumber: "",
              leadtime: "",
            },
          ],
          status: null,
        },
      ],
      delivery: {
        deliveryNumber: "",
        method: "",
        type: "",
        driverName: "",
        // Removed invoice field
        poNumber: "", // Will be set from nexusPONumber
        location: "",
        soNumber: "",
        engineer: "",
        salesperson: "",
        urgency: "",
        // Combined delivery date field for delivery dialog (date only)
        date: null,
        datetimeMenu: false,
        datetimeDisplay: "",
        status: "",
        reason: "",
      },
      deliveryItems: [],
      suppliers: [],
      combinedLocations: [],
      engineers: [],
      salespersons: [],
      priorityOptions: ["Not Urgent", "Medium", "Urgent"],
      status: ["In Progress", "Delayed", "Complete"],
      methods: ["Driver", "Courier"],
      filteredTypes: ["Collection", "Delivery", "Both"],
      urgencies: ["Urgent", "Medium", "Not Urgent"],
      statuses: ["Pending", "Dispatched", "Delivered"],
      leadTimeOptions: [
        "1-2 DAYS",
        "2-3 DAYS",
        "3-5 DAYS",
        "4-6 DAYS",
        "5-7 DAYS",
        "6-8 DAYS",
        "8-10 DAYS",
        "10-12 DAYS",
        "10-15 DAYS",
        "15-20 DAYS",
        "6-8 WEEKS",
        "8-10 WEEKS",
        "10-12 WEEKS",
        "Ex - Stock",
       
      ],
      rules: {
        required: (value) => !!value || "Required.",
        workHours: (value) => {
          if (!value) return true;
          const time = value.split(":");
          const hours = parseInt(time[0], 10);
          return (hours >= 9 && hours <= 17) || "Time must be within work hours (09:00 - 17:00).";
        },
        validTime: (value) => {
          if (!value) return true;
          const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
          return timePattern.test(value) || "Invalid time format (HH:MM)";
        },
        nexusPONumber: (value) => {
          const pattern = /^[A-Z0-9-]+$/;
          return pattern.test(value) || "Invalid Nexus PO Number format.";
        },
      },
      dialogSubmit: false,
      dialogRemoveItem: false,
      currentItemIndex: null,
      currentPOIndex: null,
      userId: "",
      showDeliveryDialog: false,
    };
  },
  computed: {
    reasonRequired() {
      return this.delivery.method === "Courier";
    },
    isInTransit() {
      return this.delivery.method === "Driver";
    },
    isSalespersonSelected() {
      return !!this.delivery.salesperson;
    },
    isEngineerSelected() {
      return !!this.delivery.engineer;
    },
  },
  async created() {
    await this.getCurrentUser();
    await this.fetchSuppliers();
    await this.fetchEngineers();
    await this.fetchSalespersons();
    await this.generatePurchaseOrderNumber();
    // Note: Nexus PO Number is not auto‑generated.
  },
  methods: {
    async getCurrentUser() {
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;
      if (user) {
        this.userId = user.uid;
      }
    },
    async fetchSuppliers() {
      try {
        const db = getFirestore(firebaseApp);
        const suppliersCollection = collection(db, "suppliers");
        const suppliersSnapshot = await getDocs(suppliersCollection);
        this.suppliers = suppliersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          supplierName: doc.data()["Supplier Name"] || "",
          contactPerson: doc.data()["Contact Person"] || "",
          supplierAccount: doc.data()["Supplier Account"] || "",
        }));
        // For combinedLocations, simply map the suppliers data.
        this.combinedLocations = this.suppliers.map((s) => ({
          name: s.supplierName,
          account: s.supplierAccount,
        }));
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    },
    async fetchEngineers() {
      try {
        const db = getFirestore(firebaseApp);
        const usersCollection = collection(db, "users");
        const querySnapshot = await getDocs(query(usersCollection, orderBy("fullName")));
        const engineers = [];
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          if (userData.role === "Engineer") {
            engineers.push({ fullName: userData.fullName, id: doc.id });
          }
        });
        this.engineers = engineers;
      } catch (error) {
        console.error("Error fetching engineers:", error);
      }
    },
    async fetchSalespersons() {
      try {
        const db = getFirestore(firebaseApp);
        const usersCollection = collection(db, "users");
        const querySnapshot = await getDocs(query(usersCollection, orderBy("fullName")));
        const salespersons = [];
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          if (userData.role === "Salesperson") {
            salespersons.push({ fullName: userData.fullName, id: doc.id });
          }
        });
        this.salespersons = salespersons;
      } catch (error) {
        console.error("Error fetching salespersons:", error);
      }
    },
    async generatePurchaseOrderNumber() {
      const db = getFirestore(firebaseApp);
      const purchaseOrdersCollection = collection(db, "purchaseOrders");
      const q = query(purchaseOrdersCollection, orderBy("purchaseNumber", "desc"), limit(1));
      const querySnapshot = await getDocs(q);
      let nextPurchaseNumber = 1;
      if (!querySnapshot.empty) {
        const lastPO = querySnapshot.docs[0].data();
        nextPurchaseNumber = lastPO.purchaseNumber + 1;
      }
      this.purchaseOrders[0].purchaseNumber = nextPurchaseNumber;
    },
    async generateDeliveryNumber() {
      const db = getFirestore(firebaseApp);
      const deliveriesCollection = collection(db, "deliveries");
      const q = query(deliveriesCollection, orderBy("deliveryNumber", "desc"), limit(1));
      const querySnapshot = await getDocs(q);
      let nextDeliveryNumber = 1;
      if (!querySnapshot.empty) {
        const lastDelivery = querySnapshot.docs[0].data();
        nextDeliveryNumber = lastDelivery.deliveryNumber + 1;
      }
      this.delivery.deliveryNumber = nextDeliveryNumber;
    },
    addItem(poIndex) {
      this.purchaseOrders[poIndex].items.push({
        quantity: "",
        code: "",
        description: "",
        drawingNumber: "",
        leadtime: "",
      });
    },
    removePOItem(poIndex, itemIndex) {
      // Directly remove the item from the PO items array
      if (this.purchaseOrders[poIndex].items.length > 1) {
        this.purchaseOrders[poIndex].items.splice(itemIndex, 1);
      } else {
        alert("Cannot remove the last item.");
      }
    },
    confirmSubmit() {
      this.dialogSubmit = true;
    },
    async submitConfirmed() {
  console.log("Submit Purchase Order button clicked!");

  // Ensure the form exists before validation
  if (!this.$refs.form) {
    console.error("Form reference is missing.");
    return;
  }

  // Validate the form
  const isValid = this.$refs.form.validate();

  if (!isValid) {
    console.log("Form validation failed.");
    return;
  }

  console.log("Form is valid!");

  this.loading = true;
  try {
    const db = getFirestore(firebaseApp);
    for (const po of this.purchaseOrders) {
      const poData = {
        purchaseNumber: po.purchaseNumber,
        nexusPONumber: po.nexusPONumber,
        supplier: po.supplier,
        supplierReference: po.supplierReference,
        deliveryDate: po.deliveryDateTimeDisplay,
        priority: po.priority,
        notes: po.notes,
        soNumber: po.soNumber,
        engineer: po.selectedEngineer,
        engineerUID: po.selectedEngineer,
        salesperson: po.selectedSalesperson,
        salespersonUID: po.selectedSalesperson,
        quoteTimestamp: po.quoteTimestamp,
        items: po.items,
        createdBy: this.userId,
        status: po.status,
      };

      await addDoc(collection(db, "purchaseOrders"), poData);
    }

    console.log("Purchase orders created successfully");
    this.$refs.form.reset(); // Reset form after submission
    this.purchaseOrders = [
      {
        purchaseNumber: "",
        nexusPONumber: "",
        supplier: null,
        supplierReference: "",
        deliveryDate: null,
        deliveryDateTimeMenu: false,
        deliveryDateTimeDisplay: "",
        priority: null,
        notes: "",
        soNumber: "",
        selectedEngineer: null,
        selectedSalesperson: null,
        quoteTimestamp: new Date().toISOString(),
        items: [
          {
            quantity: "",
            code: "",
            description: "",
            drawingNumber: "",
            leadtime: "",
          },
        ],
        status: null,
      },
    ];
    await this.generatePurchaseOrderNumber();
    this.dialogSubmit = false;
    this.$router.push("/AllPO");
  } catch (error) {
    console.error("Error creating purchase orders:", error);
  } finally {
    this.loading = false;
  }
}
,
    async openDeliveryDialog() {
  console.log("Opening Delivery Dialog...");

  const po = this.purchaseOrders[0];

  console.log("Selected Engineer UID:", po.selectedEngineer);
  console.log("Selected Salesperson UID:", po.selectedSalesperson);

  // Generate a new delivery number
  await this.generateDeliveryNumber();

  // Set the delivery PO Number to the Nexus PO Number from the PO
  this.delivery.poNumber = po.nexusPONumber;
  
  // NEW: Set the SO Number from the PO to the delivery object
  this.delivery.soNumber = po.soNumber;

  // Convert UID to Name for engineer and salesperson
  const engineerObj = this.engineers.find(e => e.id === po.selectedEngineer);
  const salespersonObj = this.salespersons.find(s => s.id === po.selectedSalesperson);

  // Assign the full names instead of UIDs
  this.delivery.engineer = engineerObj ? engineerObj.fullName : "Unknown Engineer";
  this.delivery.salesperson = salespersonObj ? salespersonObj.fullName : "Unknown Salesperson";

  // Assign other relevant details
  this.delivery.location = po.supplier;
  this.delivery.urgency = po.priority;
  this.delivery.status = po.status;

  // If the PO has a delivery date set, pass it along
  if (po.deliveryDateTimeDisplay) {
    this.delivery.datetimeDisplay = po.deliveryDateTimeDisplay;
  }

  console.log("Delivery Engineer (Name):", this.delivery.engineer);
  console.log("Delivery Salesperson (Name):", this.delivery.salesperson);

  this.showDeliveryDialog = true;
}
,
    updatePODeliveryDateTime(index) {
      const po = this.purchaseOrders[index];
      if (po.deliveryDate) {
        po.deliveryDateTimeDisplay = po.deliveryDate;
      }
      po.deliveryDateTimeMenu = false;
    },
    updateDeliveryDateTime() {
      if (this.delivery.date) {
        this.delivery.datetimeDisplay = this.delivery.date;
      }
      this.delivery.datetimeMenu = false;
    },
    removeDeliveryItem(index) {
      // Directly remove the delivery item from the array
      this.deliveryItems.splice(index, 1);
    },
    addDeliveryItem() {
      this.deliveryItems.push({
        qty: "",
        code: "",
        description: "",
        refnum: "",
        for: "Delivery",
      });
    },
    async saveDelivery() {
      if (this.$refs.deliveryForm.validate()) {
        this.loading = true;
        const db = getFirestore(firebaseApp);
        try {
          const deliveryData = {
            ...this.delivery,
            deliveryItems: this.deliveryItems,
          };
          await addDoc(collection(db, "deliveries"), deliveryData);
          console.log("Delivery created successfully");
          this.$refs.deliveryForm.reset();
          this.deliveryItems = [];
          this.showDeliveryDialog = false;
        } catch (error) {
          console.error("Error creating delivery: ", error);
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.add-po-card {
  position: relative;
  z-index: 2;
  border-radius: 20px;
  overflow: hidden;
  margin-top: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  padding: 20px;
  transition: all 0.3s ease-in-out;
}
.montserrat-input {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}
.montserrat-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}
.purchase-order-group {
  margin-bottom: 20px;
}
.item-group {
  margin-bottom: 20px;
}
.section-title {
  color: white;
  font-weight: bold;
}
.v-btn {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}
.v-card-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}
.back-button {
  position: absolute;
  top: 16px;
  left: 16px;
  background-color: red;
  color: white;
  border-radius: 50%;
}
.back-button:hover {
  transform: scale(1.1);
}
.add-delivery-card {
  background-color: #1e1e1e;
}
</style>
